import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Collapse, Grid, InputAdornment } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import {
  type CustomFieldValidatorState,
  type FieldValidator,
  NumberUtils,
  Text,
} from '@pbt/pbt-ui-components'

import type {
  ModifiedChargeSheetLineItemInput,
  RetailOrderLineItem,
} from '~/api/graphql/generated/types'
import { CollapseArrow } from '~/components/elements/CollapseArrow/CollapseArrow'
import DialogNames, { ConfirmAlertType } from '~/constants/DialogNames'
import { discountEditRoleBlacklist } from '~/constants/roleNames'
import {
  editChargeSheetSectionPercentageDiscount,
  fetchClientFinanceCharges,
  getChargeSheetSubItemById,
  getClientFinanceLoading,
  updateSectionAdditionalDiscount,
} from '~/store/duck/clientFinanceData'
import { useIsChewyCheckoutEnabled } from '~/store/hooks/business'
import { getCurrentUserId } from '~/store/reducers/auth'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useDialog from '~/utils/useDialog'
import useHasRole from '~/utils/useHasRole'

import {
  formatMoneyRange,
  getIsRetailOrderLineItem,
} from '../../invoices/invoiceUtils'
import DiscountItems from './DiscountItems'

const useStyles = makeStyles(
  (theme) => ({
    collapse: {
      paddingLeft: theme.spacing(4),
    },
    badgeWrapper: {
      display: 'flex',
      alignItems: 'center',
    },
    editIcon: {
      fontSize: '2rem',
      color: theme.colors.editIconColor,
    },
  }),
  { name: 'Discounts' },
)

type DiscountsProp = {
  isGlobalFooter?: boolean
  sectionId?: string
}

const Discounts = ({ isGlobalFooter, sectionId }: DiscountsProp) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'Invoices'])
  const dispatch = useDispatch()

  const modifierId = useSelector(getCurrentUserId)
  const chargeSheetItem = useSelector(getChargeSheetSubItemById(sectionId))
  const [openMissingSectionIdDialog] = useDialog(DialogNames.DISMISSIBLE_ALERT)

  const [totalExpanded, setTotalExpanded] = useState(false)

  const canEditDiscount = !useHasRole(discountEditRoleBlacklist)

  const fetchAfterPercentageDiscountChanged = useCloseAfterCreation(
    () =>
      chargeSheetItem &&
      dispatch(fetchClientFinanceCharges({ id: chargeSheetItem.clientId })),
    getClientFinanceLoading,
  )
  const isChewyCheckoutEnabled = useIsChewyCheckoutEnabled()

  if (!chargeSheetItem) {
    return null
  }

  const {
    additionalDiscount,
    bundleAdditionalDiscount,
    discount,
    estimateAdditionalDiscount,
    itemDiscount,
    modificationDate,
    groupedItems,
    subtotal,
    totalTax,
    chewyItemDiscount = 0,
  } = chargeSheetItem

  const sectionRetailOrderLineItems = isChewyCheckoutEnabled
    ? (groupedItems.filter((item: any) =>
        getIsRetailOrderLineItem(item),
      ) as unknown as RetailOrderLineItem[])
    : []

  const sectionContainsInvoiceLineItems =
    !isChewyCheckoutEnabled ||
    groupedItems.some((item: any) => !getIsRetailOrderLineItem(item))

  const displayDiscountDropdown =
    sectionContainsInvoiceLineItems ||
    (chewyItemDiscount !== 0 && sectionRetailOrderLineItems.length > 0)

  const handleSaveAdittionalDiscount = (additionalDiscountValue: number) => {
    if (!modifierId || !sectionId) {
      return
    }
    if (sectionId.includes('_')) {
      openMissingSectionIdDialog({
        message: t('Invoices:ITEM_DISCOUNT_MISSING_SECTION_ID'),
      })
      return
    }
    dispatch(
      updateSectionAdditionalDiscount({
        modifierId,
        updateSectionsDiscountInputs: [
          {
            id: sectionId,
            additionalDiscount: additionalDiscountValue,
            expectedModificationDate: modificationDate,
          },
        ],
      }),
    )
  }

  const handleSaveItemsPercentageDiscount = (discountPerc: number) => {
    if (groupedItems) {
      const flattenGrouppedItems = groupedItems.flatMap(
        (item) => R.prop('items', item) || item,
      )
      fetchAfterPercentageDiscountChanged()
      dispatch(
        editChargeSheetSectionPercentageDiscount({
          updateItemInput: flattenGrouppedItems.map(
            ({
              id,
              modificationDate: modificationDateProp,
              soapLogModificationDate,
            }) =>
              ({
                discountPerc: discountPerc ? discountPerc / 100 : 0,
                id,
                expectedModification: modificationDateProp,
                expectedSoapLogModification:
                  soapLogModificationDate || modificationDateProp,
              }) as ModifiedChargeSheetLineItemInput,
          ),
        }),
      )
    }
  }

  const getFormattedItemDiscount = (discountValue: number | undefined) =>
    discountValue
      ? discountValue > 0
        ? `${NumberUtils.formatMoney(discountValue, true)}`
        : NumberUtils.formatMoney(discountValue).replace(/[()]/g, '')
      : 0

  const roliPricesWithTaxWithoutDiscount = sectionRetailOrderLineItems.reduce(
    (acc, item: RetailOrderLineItem) =>
      acc + item.price + item.taxAmount - item.discountAmount,
    0,
  )

  const additionalDiscountValidator = {
    validator: ({ state, name }: CustomFieldValidatorState) =>
      state[name] >=
        -1 * (estimateAdditionalDiscount + bundleAdditionalDiscount) &&
      state[name] <=
        subtotal +
          totalTax -
          (itemDiscount +
            estimateAdditionalDiscount +
            bundleAdditionalDiscount) -
          roliPricesWithTaxWithoutDiscount,
    validatorName: 'additionalDiscountValidator',
  } as FieldValidator

  const discounts = [
    {
      id: ConfirmAlertType.ITEM_DISCOUNT,
      name: t('Invoices:ITEM_DISCOUNT'),
      value: getFormattedItemDiscount(itemDiscount),
      editable: canEditDiscount,
      tooltipText: null,
      description: chewyItemDiscount
        ? t('Invoices:ITEM_DISCOUNT_POPER_DESCRIPTION_CHEWY_DISCLAIMER')
        : t('Invoices:ITEM_DISCOUNT_POPER_DESCRIPTION'),
      confirmationDialogText: t('Invoices:ITEM_DISCOUNT_DIALOG_CONFIRMATION'),
      InputProps: {
        endAdornment: <InputAdornment position="end">%</InputAdornment>,
      },
      save: handleSaveItemsPercentageDiscount,
    },
    {
      id: 'chewyDiscount',
      name: t('Invoices:CHEWY_DISCOUNT'),
      value: getFormattedItemDiscount(chewyItemDiscount),
      editable: canEditDiscount,
      tooltipText: null,
    },
    {
      id: 'estimateDiscount',
      name: t('Invoices:ESTIMATE_DISCOUNT'),
      value: getFormattedItemDiscount(estimateAdditionalDiscount),
      editable: canEditDiscount,
      tooltipText: t('Invoices:ESTIMATE_DISCOUNT_TOOLTIP'),
    },
    {
      id: 'bundleDiscount',
      name: t('Invoices:BUNDLE_DISCOUNT'),
      value: getFormattedItemDiscount(bundleAdditionalDiscount),
      editable: canEditDiscount,
      tooltipText: t('Invoices:BUNDLE_DISCOUNT_TOOLTIP'),
    },
    {
      id: ConfirmAlertType.ADDITIONAL_DISCOUNT,
      name: t('Invoices:ADDITIONAL_DISCOUNT'),
      value: getFormattedItemDiscount(additionalDiscount),
      editable: canEditDiscount,
      tooltipText: null,
      description: '',
      confirmationDialogText: t(
        'Invoices:ADDITIONAL_DISCOUNT_DIALOG_CONFIRMATION',
      ),
      InputProps: {
        startAdornment: <InputAdornment position="start">$</InputAdornment>,
      },
      save: handleSaveAdittionalDiscount,
      customValidators: [additionalDiscountValidator],
    },
  ]

  return (
    <Grid
      container
      flexDirection={totalExpanded ? 'column' : 'row'}
      ml={isGlobalFooter ? 0 : -3}
    >
      {discounts.length > 0 && (
        <Collapse unmountOnExit className={classes.collapse} in={totalExpanded}>
          <DiscountItems
            discounts={discounts}
            sectionContainsInvoiceLineItems={sectionContainsInvoiceLineItems}
          />
        </Collapse>
      )}
      <Grid
        container
        item
        justifyContent="space-between"
        onClick={() => !isGlobalFooter && setTotalExpanded(!totalExpanded)}
      >
        <Grid
          container
          item
          alignItems="center"
          display="flex"
          md={6}
          sm={6}
          sx={{ cursor: 'pointer' }}
          wrap="nowrap"
          xl={6}
          xs={6}
        >
          {isGlobalFooter || !displayDiscountDropdown ? null : (
            <CollapseArrow expanded={totalExpanded} />
          )}
          <Text ml={!displayDiscountDropdown ? 3 : 0} variant="body2">
            {t('Common:DISCOUNTS')}
          </Text>
        </Grid>
        <Text
          sx={{
            cursor:
              isGlobalFooter || !displayDiscountDropdown
                ? undefined
                : 'pointer',
          }}
          variant="body2"
        >
          {discount > 0
            ? `${formatMoneyRange(discount, true)}`
            : formatMoneyRange(discount).replace(/[()]/g, '')}
        </Text>
      </Grid>
    </Grid>
  )
}

export default Discounts
